import { useEffect, useState } from "react";
import { Megaphone, Plus, Trash2, Pause, Play, X } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { fmtMoney, fmtNumber } from "@/lib/format";

export default function AdsTab() {
  const auth = useAdminAuth();
  const [ads, setAds] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", advertiser: "", budget_usd: 1000, target: "all" });

  const load = () => {
    auth.apiGet("/admin/ads").then((r) => setAds(r.data)).catch(() => toast.error("No se pudieron cargar campañas"));
  };
  useEffect(load, [auth]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    try {
      await auth.apiPost("/admin/ads", { ...form, budget_usd: Number(form.budget_usd) });
      toast.success("Campaña creada");
      setShowNew(false);
      setForm({ title: "", advertiser: "", budget_usd: 1000, target: "all" });
      load();
    } catch {
      toast.error("Error al crear campaña");
    }
  };

  const toggleStatus = async (ad) => {
    const newStatus = ad.status === "active" ? "paused" : "active";
    try {
      await auth.apiPatch(`/admin/ads/${ad.id}`, { status: newStatus });
      toast.success(`Campaña ${newStatus === "active" ? "reanudada" : "pausada"}`);
      load();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const remove = async (ad) => {
    if (!window.confirm(`¿Eliminar la campaña "${ad.title}"?`)) return;
    try {
      await auth.apiDelete(`/admin/ads/${ad.id}`);
      toast.success("Campaña eliminada");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div data-testid="tab-ads" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Gestión de anuncios</p>
        <button
          data-testid="ads-new-btn"
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-ink text-cream hover:bg-coral transition"
        >
          <Plus className="w-3.5 h-3.5" /> Nueva campaña
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} data-testid="ads-new-form" className="border hairline bg-cream-card rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-xl tracking-tight">Nueva campaña</h3>
            <button type="button" onClick={() => setShowNew(false)} data-testid="ads-new-close"><X className="w-4 h-4 text-ink-mid" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Input label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="ads-form-title" />
            <Input label="Anunciante" value={form.advertiser} onChange={(v) => setForm({ ...form, advertiser: v })} testid="ads-form-advertiser" />
            <Input label="Presupuesto USD" type="number" value={form.budget_usd} onChange={(v) => setForm({ ...form, budget_usd: v })} testid="ads-form-budget" />
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">Audiencia</span>
              <select
                value={form.target}
                onChange={(e) => setForm({ ...form, target: e.target.value })}
                data-testid="ads-form-target"
                className="w-full bg-cream border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral"
              >
                <option value="all">Toda la red</option>
                <option value="creators">Creadores</option>
                <option value="business">Empresas / PYMES</option>
                <option value="travelers">Viajeros</option>
              </select>
            </label>
          </div>
          <button type="submit" data-testid="ads-form-submit" className="w-full bg-coral text-white rounded-full py-2.5 text-sm hover:bg-coral/90">
            Crear campaña
          </button>
        </form>
      )}

      <section className="border hairline bg-cream-card rounded-2xl p-3 md:p-5">
        <div className="hidden md:grid grid-cols-12 gap-3 px-3 pb-2 border-b hairline font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
          <div className="col-span-3">campaña</div>
          <div className="col-span-2">anunciante</div>
          <div className="col-span-2">presupuesto</div>
          <div className="col-span-2">métricas</div>
          <div className="col-span-1">estado</div>
          <div className="col-span-2 text-right">acciones</div>
        </div>
        {ads.length === 0 ? (
          <p className="text-sm text-ink-mid text-center py-8" data-testid="ads-empty">Sin campañas activas.</p>
        ) : ads.map((ad) => (
          <div key={ad.id} data-testid={`ad-row-${ad.id}`} className="grid grid-cols-12 gap-3 px-3 py-3 border-b hairline last:border-b-0 items-center">
            <div className="col-span-12 md:col-span-3 flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-coral" />
              <span className="text-sm font-medium truncate">{ad.title}</span>
            </div>
            <div className="col-span-6 md:col-span-2 text-xs text-ink-mid truncate">{ad.advertiser}</div>
            <div className="col-span-6 md:col-span-2 text-xs font-mono">
              {fmtMoney(ad.spent_usd)} / {fmtMoney(ad.budget_usd)}
            </div>
            <div className="col-span-8 md:col-span-2 text-xs font-mono text-ink-mid">
              {fmtNumber(ad.impressions)} imp · {fmtNumber(ad.clicks)} clk
            </div>
            <div className="col-span-4 md:col-span-1">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-wider
                ${ad.status === "active" ? "bg-emerald-100 text-emerald-700" : ad.status === "paused" ? "bg-amber-100 text-amber-700" : "bg-zinc-200 text-zinc-700"}`}>
                {ad.status === "active" ? "Activa" : ad.status === "paused" ? "Pausada" : "Finalizada"}
              </span>
            </div>
            <div className="col-span-12 md:col-span-2 flex justify-end gap-1.5">
              <button data-testid={`ad-toggle-${ad.id}`} onClick={() => toggleStatus(ad)} className="p-1.5 rounded-full hover:bg-cream" title={ad.status === "active" ? "Pausar" : "Activar"}>
                {ad.status === "active" ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              <button data-testid={`ad-delete-${ad.id}`} onClick={() => remove(ad)} className="p-1.5 rounded-full hover:bg-red-50 text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-cream border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral"
      />
    </label>
  );
}
